import type { TemplatePage } from '../templates';

const filled = [
  { item: 'Tree 1: white pine, about 24 in DBH, 70 ft, back corner: fell into the open lawn', amount: '$1,100' },
  { item: 'Tree 2: dead ash, about 18 in DBH, over the shed: climb and lower limbs by rope', amount: '$1,350' },
  { item: 'All brush chipped and removed; wood cut to firewood length and left by the fence', amount: '$250' },
  { item: 'Optional: grind both stumps 8 in below grade, grindings left in place', amount: '$420' },
];

const faq = [
  {
    q: 'What does DBH mean on a tree estimate?',
    a: 'Diameter at breast height: the trunk diameter measured about four and a half feet above the ground. It is a common way to describe tree size on an estimate because it is easy to measure and relates to how much wood comes down.',
  },
  {
    q: 'Why list each tree separately?',
    a: 'Because each tree is priced on its own risk and method. A pine that can be felled into open lawn costs far less than a dead ash over a shed. Separate lines also let the customer remove one tree now and another later.',
  },
  {
    q: 'Is this template free?',
    a: 'Yes, with no sign-up. Read it here, download the PDF or copy it.',
  },
  {
    q: 'Can I add stump grinding as an option?',
    a: 'The template has an optional stump line. In Scopie that line can be marked optional, so the homeowner ticks it on the emailed estimate and sees the new total before signing.',
  },
];

const page: TemplatePage = {
  slug: 'tree-service-estimate-template',
  path: '/templates/tree-service-estimate-template',
  primaryKeyword: 'tree removal estimate template',
  secondaryKeywords: ['tree service quote template', 'tree trimming estimate form', 'arborist estimate template'],
  title: 'Free Tree Removal Estimate Template (PDF) | Scopie', // 50
  metaDescription:
    'A free tree service estimate template with a per-tree table (species, DBH, height, method), wood and brush plan, stump line, access and damage terms.', // 150
  h1: 'Tree removal estimate template',
  answerFirst:
    'A tree service estimate should price each tree on its own line with its species, size and how it comes down, then say what happens to the wood, the brush and the stump. This free template adds access, lawn protection and permit sections, with a two-tree example where stump grinding is an add-on.',
  lastReviewed: '2026-10-07',
  schemaType: 'DigitalDocument',
  leadSheet: true,
  blocks: [
    {
      type: 'sample-quote',
      id: 'filled',
      h2: 'Example first: two removals and a stump option',
      label: 'Filled in for a backyard pine and a dead ash',
      lines: filled,
      total: '$3,120',
      totalLabel: 'Total with both stumps ground',
      note: 'Illustrative figures only.',
    },
    {
      type: 'document',
      id: 'template',
      h2: 'The blank estimate',
      intro: 'Add a row per tree. Describe the method so the homeowner understands the price.',
      pdfName: 'tree-service-estimate-template',
      sections: [
        { heading: 'Tree company', lines: ['[Company] · [Phone] · [Email] · [ISA certification or license, if you show it]'] },
        { heading: 'Property', lines: ['[Owner] · [Address]', 'Estimate [number] · [date] · valid until [date]'] },
        {
          heading: 'Trees',
          lines: [
            'Tree [1]: [species], about [DBH] in DBH, [height] ft, [location] · work [remove / trim / reduce / deadwood] · method [fell / climb and rig / lift / crane] · [price]',
            'Tree [2]: [species], about [DBH] in DBH, [height] ft, [location] · work [ ] · method [ ] · [price]',
          ],
        },
        { heading: 'Wood and brush', lines: ['Brush: [chipped and removed / chips left on site] · wood: [removed / cut to length and left / split and stacked] · [price]'] },
        { heading: 'Stumps', lines: ['Grind [number] stumps to [inches] below grade · grindings [removed / left] · [price]'] },
        {
          heading: 'Access and protection',
          lines: ['Equipment access via [driveway / side gate / neighbor\'s yard with permission]', 'Lawn protection: [mats / none]; damage to unmarked irrigation or lighting is [your terms]'],
        },
        { heading: 'Permits and terms', lines: ['Permit required [yes / no / unknown], obtained by [us / owner].', 'Hidden metal or concrete in the trunk: [your terms].', 'Deposit: [amount or none]. Cancellation: [your terms].'] },
        { heading: 'Accepted', lines: ['[Owner name] · [Signature] · [Date]'] },
      ],
    },
    {
      type: 'list',
      id: 'method-words',
      h2: 'Method words homeowners understand',
      style: 'bullets',
      intro: 'A one-word method on each line explains most price differences:',
      items: [
        { title: 'Fell', text: 'Dropped in one piece where there is open space.' },
        { title: 'Climb and rig', text: 'Taken down in sections with ropes because something is underneath.' },
        { title: 'Lift', text: 'Worked from a bucket truck or aerial lift.' },
        { title: 'Crane', text: 'Pieces lifted out over a house or structure; the crane is a separate cost.' },
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Tree estimate mistakes',
      style: 'mistakes',
      items: [
        { title: 'Wood and brush left unsaid', text: 'Customers have strong views on firewood and chips; settle it on the estimate.' },
        { title: 'Stump bundled into removal', text: 'Grinding takes another machine and visit; give it its own line.' },
        { title: 'No access note', text: 'A narrow gate or a soft lawn changes equipment and time.' },
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Tree estimate template questions', items: faq },
  ],
  faq,
  internalLinks: [
    { href: '/for/tree-service', anchor: 'Scopie for tree service companies', status: 'live' },
    { href: '/templates/landscaping-quote-template', anchor: 'landscaping quote template', status: 'live' },
    { href: '/templates', anchor: 'All free templates', status: 'live' },
  ],
  cta: {
    heading: 'Price the next tree from the driveway',
    text: 'Keep your per-tree items, methods and exclusions in Scopie, and email each estimate before you leave.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
};

export default page;
